import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Save, Plus, Trash2, Edit2, Check, X, MoveUp, MoveDown,
    Eye, Shield, Target, Download, FileText, Mail, Star, Users
} from 'lucide-react';
import toast from 'react-hot-toast';

const RatingCertificateAdvantagePage = () => {
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('settings'); // settings, benefits, steps

    // Settings State
    const [settings, setSettings] = useState({
        section_tag: 'Your Advantage',
        section_title: 'Your Benefits & How to Access the Certificate',
        section_intro: 'With our credit rating certificate at hand, you gain:',
        next_steps_title: 'Next Steps',
        contact_label: 'Investor Relations',
        contact_email: 'investors@ahamhfc.com',
        update_note_label: 'Stay updated:',
        update_note_text: "We publish any rating revisions or outlook changes promptly, ensuring you're always in the know.",
        download_text: 'Click **Download the Certificate** above to obtain the PDF',
        contact_button_text: 'Contact Team'
    });

    // Benefits State
    const [benefits, setBenefits] = useState([]);
    const [editingBenefit, setEditingBenefit] = useState(null);
    const [isBenefitModalOpen, setIsBenefitModalOpen] = useState(false);

    // Next Steps State
    const [nextSteps, setNextSteps] = useState([]);
    const [editingStep, setEditingStep] = useState(null);
    const [isStepModalOpen, setIsStepModalOpen] = useState(false);

    // Icons list for selection
    const iconOptions = [
        { name: 'Eye', component: Eye },
        { name: 'Shield', component: Shield },
        { name: 'Target', component: Target },
        { name: 'Download', component: Download },
        { name: 'FileText', component: FileText },
        { name: 'Mail', component: Mail },
        { name: 'Star', component: Star },
        { name: 'Users', component: Users }
    ];

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [settingsRes, benefitsRes, stepsRes] = await Promise.all([
                axios.get('/api/admin/rating-advantage-settings'),
                axios.get('/api/admin/key-benefits/rating_certificate_advantage'), // Get ALL, not just active
                axios.get('/api/admin/key-benefits/rating_certificate_next_steps') // Get ALL, not just active
            ]);

            if (settingsRes.data.success) {
                setSettings(settingsRes.data.data);
            }
            if (benefitsRes.data.success) {
                setBenefits(benefitsRes.data.data);
            }
            if (stepsRes.data.success) {
                setNextSteps(stepsRes.data.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    // --- Settings Handlers ---
    const handleSettingsChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({ ...prev, [name]: value }));
    };

    const saveSettings = async () => {
        try {
            await axios.put('/api/admin/rating-advantage-settings', settings);
            toast.success('Settings updated successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to save settings');
        }
    };

    // --- Benefits Handlers ---
    const openBenefitModal = (benefit = null) => {
        setEditingBenefit(benefit ? { ...benefit } : {
            title: '',
            description: '',
            icon_name: 'Star',
            display_order: benefits.length + 1,
            is_active: 1,
            loan_type: 'rating_certificate_advantage'
        });
        setIsBenefitModalOpen(true);
    };

    const saveBenefit = async () => {
        try {
            if (editingBenefit.id) {
                await axios.put(`/api/admin/key-benefits/${editingBenefit.id}`, editingBenefit);
                toast.success('Benefit updated');
            } else {
                await axios.post('/api/admin/key-benefits', editingBenefit);
                toast.success('Benefit created');
            }
            setIsBenefitModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving benefit:', error);
            toast.error('Failed to save benefit');
        }
    };

    const deleteBenefit = async (id) => {
        if (!window.confirm('Are you sure you want to delete this benefit?')) return;
        try {
            await axios.delete(`/api/admin/key-benefits/${id}`);
            toast.success('Benefit deleted');
            fetchData();
        } catch (error) {
            console.error('Error deleting benefit:', error);
            toast.error('Failed to delete benefit');
        }
    };

    // --- Next Steps Handlers ---
    const openStepModal = (step = null) => {
        setEditingStep(step ? { ...step } : {
            title: 'New Step',
            description: '',
            icon_name: 'Check',
            display_order: nextSteps.length + 1,
            is_active: 1,
            loan_type: 'rating_certificate_next_steps'
        });
        setIsStepModalOpen(true);
    };

    const saveStep = async () => {
        try {
            if (editingStep.id) {
                await axios.put(`/api/admin/key-benefits/${editingStep.id}`, editingStep);
                toast.success('Step updated');
            } else {
                await axios.post('/api/admin/key-benefits', editingStep);
                toast.success('Step created');
            }
            setIsStepModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving step:', error);
            toast.error('Failed to save step');
        }
    };

    const deleteStep = async (id) => {
        if (!window.confirm('Are you sure you want to delete this step?')) return;
        try {
            await axios.delete(`/api/admin/key-benefits/${id}`);
            toast.success('Step deleted');
            fetchData();
        } catch (error) {
            console.error('Error deleting step:', error);
            toast.error('Failed to delete step');
        }
    };

    // --- Render Helpers ---
    const renderIconOption = (iconName) => {
        const Icon = iconOptions.find(i => i.name === iconName)?.component || Star;
        return <Icon className="w-5 h-5" />;
    };

    if (loading) return <div className="p-8 text-center">Loading...</div>;

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-8">
            <div className="flex justify-between items-center">
                <h1 className="text-3xl font-bold text-gray-900">Rating Certificate - Your Advantage</h1>
            </div>

            {/* Tabs */}
            <div className="flex space-x-4 border-b border-gray-200">
                <button
                    onClick={() => setActiveTab('settings')}
                    className={`pb-2 px-4 font-medium transition-colors ${activeTab === 'settings'
                            ? 'border-b-2 border-primary-600 text-primary-600'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                >
                    Settings
                </button>
                <button
                    onClick={() => setActiveTab('benefits')}
                    className={`pb-2 px-4 font-medium transition-colors ${activeTab === 'benefits'
                            ? 'border-b-2 border-primary-600 text-primary-600'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                >
                    Benefit Cards
                </button>
                <button
                    onClick={() => setActiveTab('steps')}
                    className={`pb-2 px-4 font-medium transition-colors ${activeTab === 'steps'
                            ? 'border-b-2 border-primary-600 text-primary-600'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                >
                    Next Steps List
                </button>
            </div>

            {/* Settings Tab */}
            {activeTab === 'settings' && (
                <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Section Tag</label>
                            <input
                                type="text"
                                name="section_tag"
                                value={settings.section_tag}
                                onChange={handleSettingsChange}
                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Section Title</label>
                            <input
                                type="text"
                                name="section_title"
                                value={settings.section_title}
                                onChange={handleSettingsChange}
                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                        <div className="col-span-2 space-y-2">
                            <label className="text-sm font-medium text-gray-700">Section Intro</label>
                            <textarea
                                name="section_intro"
                                value={settings.section_intro}
                                onChange={handleSettingsChange}
                                rows={2}
                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Next Steps Title</label>
                            <input
                                type="text"
                                name="next_steps_title"
                                value={settings.next_steps_title}
                                onChange={handleSettingsChange}
                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Contact Button Text</label>
                            <input
                                type="text"
                                name="contact_button_text"
                                value={settings.contact_button_text}
                                onChange={handleSettingsChange}
                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Contact Label</label>
                            <input
                                type="text"
                                name="contact_label"
                                value={settings.contact_label}
                                onChange={handleSettingsChange}
                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Contact Email</label>
                            <input
                                type="email"
                                name="contact_email"
                                value={settings.contact_email}
                                onChange={handleSettingsChange}
                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                        <div className="col-span-2 space-y-2">
                            <label className="text-sm font-medium text-gray-700">Update Note Label</label>
                            <input
                                type="text"
                                name="update_note_label"
                                value={settings.update_note_label}
                                onChange={handleSettingsChange}
                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                        <div className="col-span-2 space-y-2">
                            <label className="text-sm font-medium text-gray-700">Update Note Text</label>
                            <textarea
                                name="update_note_text"
                                value={settings.update_note_text}
                                onChange={handleSettingsChange}
                                rows={2}
                                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                    </div>
                    <div className="flex justify-end pt-4">
                        <button
                            onClick={saveSettings}
                            className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2"
                        >
                            <Save className="w-4 h-4" />
                            Save Changes
                        </button>
                    </div>
                </div>
            )}

            {/* Benefits Tab */}
            {activeTab === 'benefits' && (
                <div className="space-y-4">
                    <div className="flex justify-end">
                        <button
                            onClick={() => openBenefitModal()}
                            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2"
                        >
                            <Plus className="w-4 h-4" />
                            Add Benefit
                        </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {benefits.map((benefit) => {
                            const Icon = iconOptions.find(i => i.name === benefit.icon_name)?.component || Star;
                            return (
                                <div key={benefit.id} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col h-full gap-4">
                                    <div className="flex justify-between items-start">
                                        <div className="p-2 bg-primary-50 rounded-lg text-primary-600">
                                            <Icon className="w-6 h-6" />
                                        </div>
                                        <div className={`px-2 py-1 rounded-full text-xs font-medium ${benefit.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                            }`}>
                                            {benefit.is_active ? 'Active' : 'Inactive'}
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-gray-900 mb-2">{benefit.title}</h3>
                                        <p className="text-gray-600 text-sm">{benefit.description}</p>
                                    </div>
                                    <div className="mt-auto pt-4 flex justify-end gap-2 border-t border-gray-100">
                                        <button
                                            onClick={() => openBenefitModal(benefit)}
                                            className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => deleteBenefit(benefit.id)}
                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Next Steps Tab */}
            {activeTab === 'steps' && (
                <div className="space-y-4">
                    <div className="flex justify-end">
                        <button
                            onClick={() => openStepModal()}
                            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2"
                        >
                            <Plus className="w-4 h-4" />
                            Add Step
                        </button>
                    </div>
                    <div className="space-y-4">
                        {/* Note about Steps */}
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 text-blue-800 text-sm">
                            <p><strong>Note:</strong> Steps are displayed as a numbered list. The 'Description' field supports bold text using markdown style (e.g., **text**).</p>
                        </div>

                        {nextSteps.map((step, index) => (
                            <div key={step.id} className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
                                <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center font-bold text-sm">
                                    {index + 1}
                                </div>
                                <div className="flex-1">
                                    <p className="font-medium text-gray-900">{step.description}</p>
                                </div>
                                <div className={`px-2 py-1 rounded-full text-xs font-medium ${step.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                    {step.is_active ? 'Active' : 'Inactive'}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => openStepModal(step)}
                                        className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"
                                    >
                                        <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => deleteStep(step.id)}
                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Benefit Modal */}
            {isBenefitModalOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 space-y-6">
                        <h2 className="text-xl font-bold text-gray-900">
                            {editingBenefit.id ? 'Edit Benefit' : 'New Benefit'}
                        </h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                <input
                                    type="text"
                                    value={editingBenefit.title}
                                    onChange={e => setEditingBenefit(prev => ({ ...prev, title: e.target.value }))}
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <textarea
                                    value={editingBenefit.description}
                                    onChange={e => setEditingBenefit(prev => ({ ...prev, description: e.target.value }))}
                                    rows={3}
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                                <div className="grid grid-cols-4 gap-2">
                                    {iconOptions.map(option => (
                                        <button
                                            key={option.name}
                                            onClick={() => setEditingBenefit(prev => ({ ...prev, icon_name: option.name }))}
                                            className={`p-2 rounded-lg border flex flex-col items-center gap-1 ${editingBenefit.icon_name === option.name
                                                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                                                    : 'border-gray-200 hover:bg-gray-50'
                                                }`}
                                        >
                                            <option.component className="w-5 h-5" />
                                            <span className="text-xs">{option.name}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="isActive"
                                    checked={editingBenefit.is_active}
                                    onChange={e => setEditingBenefit(prev => ({ ...prev, is_active: e.target.checked ? 1 : 0 }))}
                                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                                <label htmlFor="isActive" className="text-sm font-medium text-gray-700">Active</label>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                            <button
                                onClick={() => setIsBenefitModalOpen(false)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={saveBenefit}
                                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                            >
                                Save Benefit
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Step Modal */}
            {isStepModalOpen && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 space-y-6">
                        <h2 className="text-xl font-bold text-gray-900">
                            {editingStep.id ? 'Edit Step' : 'New Step'}
                        </h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Title (Internal Name)</label>
                                <input
                                    type="text"
                                    value={editingStep.title}
                                    onChange={e => setEditingStep(prev => ({ ...prev, title: e.target.value }))}
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description (Displayed Text)</label>
                                <textarea
                                    value={editingStep.description}
                                    onChange={e => setEditingStep(prev => ({ ...prev, description: e.target.value }))}
                                    rows={3}
                                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                                />
                                <p className="text-xs text-gray-500 mt-1">Use **text** for bold.</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="isStepActive"
                                    checked={editingStep.is_active}
                                    onChange={e => setEditingStep(prev => ({ ...prev, is_active: e.target.checked ? 1 : 0 }))}
                                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                                <label htmlFor="isStepActive" className="text-sm font-medium text-gray-700">Active</label>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                            <button
                                onClick={() => setIsStepModalOpen(false)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={saveStep}
                                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                            >
                                Save Step
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RatingCertificateAdvantagePage;
